import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Share2, UserPlus, Percent, Wallet, Copy, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "@/components/Reveal";
import { PARRAINAGE_STEPS } from "@/data/content";

const STEP_ICONS = [Share2, UserPlus, Percent, Wallet];

export default function Parrainage() {
  const [copied, setCopied] = useState(false);
  const codeRef = useRef(null);

  const copyCode = () => {
    const code = codeRef.current?.textContent || "";
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <section id="parrainage" className="relative overflow-hidden bg-night-soft py-16 md:py-24 lg:py-32" data-testid="parrainage-section">
      <div
        className="pointer-events-none absolute -left-40 top-1/3 h-[500px] w-[500px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.07) 0%, transparent 65%)" }}
        aria-hidden="true"
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Parrainage" title="Invitez un motard. Gagnez ensemble." align="center" />
        <Reveal delay={0.1}>
          <p className="mx-auto mt-6 max-w-2xl text-center text-base leading-relaxed text-muted-foreground sm:text-lg">
            Chaque membre dispose d'un code personnel. Votre filleul bénéficie de -10 % sur son adhésion, et vous gagnez
            un crédit cumulable déduit de votre prochaine cotisation.
          </p>
        </Reveal>

        <div className="relative mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="parrainage-steps">
          {PARRAINAGE_STEPS.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <Reveal key={s.step} delay={i * 0.12}>
                <div className="relative h-full rounded-2xl border border-border bg-card p-7 text-center transition-colors duration-300 hover:border-gold/50">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-gold/10 text-gold">
                    <Icon className="h-6 w-6" strokeWidth={1.7} />
                  </span>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-gold/70">Étape 0{i + 1}</p>
                  <h3 className="mt-2 font-heading text-lg font-bold uppercase tracking-wide text-foreground">{s.step}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                  {i < PARRAINAGE_STEPS.length - 1 && (
                    <ArrowRight className="absolute -right-5 top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 text-gold/60 lg:block" aria-hidden="true" />
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15} className="mt-14">
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="mx-auto flex max-w-xl flex-col items-center gap-5 rounded-2xl border border-dashed border-gold/50 bg-gold/5 p-7 sm:flex-row sm:justify-between"
            data-testid="parrainage-code-panel"
          >
            <div className="text-center sm:text-left">
              <p className="eyebrow mb-2">Votre code personnel</p>
              <p ref={codeRef} className="font-mono text-xl font-bold tracking-[0.2em] text-foreground" data-testid="parrainage-code">
                MBC84-VOTRECODE
              </p>
            </div>
            <button
              type="button"
              onClick={copyCode}
              data-testid="parrainage-copy-button"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-deep via-gold to-gold-hover px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-night transition-transform duration-300 hover:scale-105"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copié" : "Copier"}
            </button>
          </motion.div>
          <p className="mt-4 text-center text-xs text-muted-foreground">Code d'exemple — généré pour chaque membre dans l'application.</p>
        </Reveal>
      </div>
    </section>
  );
}
